import { g, dia, ui, util, V } from '@joint/plus';
import { DirectedGraph } from '@joint/layout-directed-graph';
import { data } from './gdata';
import { tokensData } from './gtoken';
import { Node, Link, Token, cellNamespace } from './shapes';
import WebGLHeatmap from '@nbxx/webgl-heatmap';
export var init = function () {
    var canvas = document.getElementById('canvas');
    var cssLoader = document.querySelector('.loader');
    // 7 Days
    var processDuration = 7 * 24 * 60 * 60 * 1000;
    // Toolbar controls
    var range = document.querySelector('input[type="range"]');
    var speedInput = document.querySelector('input[type="number"]');
    var startButton = document.querySelector('.start');
    var stopButton = document.querySelector('.stop');
    var timer = document.querySelector('.textTime');
    // 200000 milliseconds / 200 seconds - used to increase/decrease rate of time in animation
    var DEFAULT_SPEED = 200000;
    var speed = DEFAULT_SPEED;
    var reqAnimId = null;
    // Start and End time of dataset - all token events start and complete within this time period
    range.min = String(tokensData.start);
    range.max = String(tokensData.complete + processDuration);
    range.value = range.min;
    // Tokens - elements with respective path data (x/y positions , start/end points, rotation)
    var tokens = [];
    // Event Listeners
    range.addEventListener('input', function (evt) {
        var target = evt.target;
        range.value = target.value;
        // Update position of token based on time
        tokens.forEach(function (token) {
            var _a, _b;
            (_b = (_a = token.data).move) === null || _b === void 0 ? void 0 : _b.call(_a, Number(range.value));
        });
        paper.wakeUp();
        var milliseconds = Number(range.value);
        var dateObject = new Date(milliseconds);
        timer.innerHTML = dateObject.toLocaleString('en-GB');
        if (!reqAnimId) {
            // The animation is stopped.
            reqAnimId = util.nextFrame(function () {
                updateHeatmap();
                reqAnimId = null;
            });
        }
    });
    speedInput.addEventListener('input', function (evt) {
        var target = evt.target;
        // Update animation speed
        if (Number(target.value) <= 10 && Number(target.value) >= 0) {
            speed = DEFAULT_SPEED * Number(target.value);
        }
        else {
            speed = DEFAULT_SPEED;
            target.value = '1';
        }
    });
    startButton.addEventListener('click', function () {
        startButton.setAttribute('disabled', '');
        startRangeAnimation();
    });
    stopButton.addEventListener('click', function () {
        startButton.removeAttribute('disabled');
        stopRangeAnimation();
    });
    var keyboard = new ui.Keyboard();
    keyboard.on('space', function (evt) {
        evt.preventDefault();
        startButton.focus();
        if (reqAnimId) {
            startButton.removeAttribute('disabled');
            stopRangeAnimation();
        }
        else {
            startButton.setAttribute('disabled', '');
            startRangeAnimation();
        }
    });
    var WIDTH = 2000;
    var HEIGHT = 2000;
    var graph = new dia.Graph({}, { cellNamespace: cellNamespace });
    var paper = new dia.Paper({
        model: graph,
        width: WIDTH,
        height: HEIGHT,
        gridSize: 1,
        interactive: false,
        defaultConnector: { name: 'rounded' },
        async: true,
        autoFreeze: true,
        viewManagement: {
            disposeHidden: false,
        },
        frozen: true,
        background: { color: '#F3F7F6' },
        cellViewNamespace: cellNamespace,
        cellVisibility: function (cell) { return !cell.attributes.hidden; },
    });
    var scroller = new ui.PaperScroller({
        paper: paper,
        cursor: 'grab',
        baseWidth: 1,
        baseHeight: 1,
        inertia: { friction: 0.8 },
        contentOptions: {
            allowNewOrigin: 'any',
            useModelGeometry: true,
            padding: 50
        }
    });
    canvas.appendChild(scroller.el);
    scroller.render().center();
    var tokenLinks;
    createLayout(graph, data);
    setTokens();
    paper.unfreeze({
        afterRender: function () {
            cssLoader.classList.remove('loader');
            startRangeAnimation();
            paper.unfreeze();
        }
    });
    var heatmap = new WebGLHeatmap({ width: WIDTH, height: HEIGHT });
    heatmap.canvas.style.transformOrigin = '0 0';
    paper.el.insertBefore(heatmap.canvas, paper.svg);
    paper.on('transform', function (matrix) {
        heatmap.canvas.style.transform = V.matrixToTransformString(matrix);
    });
    scroller.zoomToFit({ useModelGeometry: true });
    function buildGraph(dataset) {
        var elements = [];
        var links = [];
        Object.keys(dataset.response.nodes).forEach(function (nodeLabel) {
            // Add Elements
            var node = new Node({ id: nodeLabel });
            node.setText(nodeLabel);
            elements.push(node);
        });
        Object.values(dataset.response.arcs).forEach(function (arc) {
            // Add Links
            var link = new Link();
            link.connect(arc.source, arc.target);
            links.push(link);
        });
        return { cells: elements.concat(links), tokens: links };
    }
    function createLayout(graph, data) {
        var dataset = buildGraph(data);
        graph.resetCells(dataset.cells);
        DirectedGraph.layout(graph, {
            rankDir: 'LR',
            nodeSep: 20,
            rankSep: 20,
            edgeSep: 5,
            align: 'UR',
            setVertices: function (link, vertices) {
                var polyline = new g.Polyline(vertices);
                polyline.simplify({ threshold: 0.001 });
                var polylinePoints = polyline.points;
                var numPolylinePoints = polylinePoints.length;
                // Points are used to calculate token x,y positions and rotation
                link.set({
                    'points': polylinePoints,
                    'vertices': polylinePoints.slice(1, numPolylinePoints - 1)
                });
            }
        });
        tokenLinks = dataset.tokens;
    }
    // Animation functions
    function startRangeAnimation() {
        if (reqAnimId) {
            return;
        }
        var startTime = Date.now();
        var fn = function () {
            if (range.value > range.max) {
                stopRangeAnimation();
                return;
            }
            // Calculate time passed since animation was started
            var currentTime = Date.now();
            var elapsedTime = currentTime - startTime;
            startTime = currentTime;
            // Elapsed time in milliseconds added to current range value
            range.value = String(Number(range.value) + (speed * elapsedTime));
            tokens.forEach(function (token) {
                var _a, _b;
                (_b = (_a = token.data).move) === null || _b === void 0 ? void 0 : _b.call(_a, Number(range.value));
            });
            updateHeatmap();
            var milliseconds = Number(range.value);
            var dateObject = new Date(milliseconds);
            timer.innerHTML = dateObject.toLocaleString('en-GB');
            reqAnimId = util.nextFrame(fn);
        };
        reqAnimId = util.nextFrame(fn);
    }
    function stopRangeAnimation() {
        util.cancelFrame(reqAnimId);
        reqAnimId = null;
    }
    function updateHeatmap() {
        heatmap.multiply(0);
        var currentTime = Number(range.value);
        tokens.forEach(function (token) {
            var _a = token.data, element = _a.element, lut = _a.lut, start = _a.start, end = _a.end;
            // Mark the processing of the tokens before they reach target.
            var targetDuration = end - currentTime + processDuration / 2;
            if (targetDuration > 0 && targetDuration < processDuration) {
                var p = lut[lut.length - 1];
                var r = util.timing.inout(1 - Math.abs(targetDuration / processDuration - 0.5));
                heatmap.addPoint(p.x, p.y, 150, r * 0.3);
            }
            // Mark the processing of the tokens after they leave source.
            var sourceDuration = currentTime - start + processDuration / 2;
            if (sourceDuration > 0 && sourceDuration < processDuration) {
                var p = lut[0];
                var r = util.timing.inout(1 - Math.abs(sourceDuration / processDuration - 0.5));
                heatmap.addPoint(p.x, p.y, 150, r * 0.3);
            }
            // Mark the token transfer via the link.
            if (element.get('hidden'))
                return;
            lut.forEach(function (_a) {
                var x = _a.x, y = _a.y;
                heatmap.addPoint(x, y, 75, 0.0015);
            });
        });
        heatmap.update();
        heatmap.display();
    }
    // Tokens
    function setTokens() {
        // Token elements to be added to graph
        var tokenElements = [];
        // Token colors - Each event id in ascending order has its own color
        var eventNumbers = [];
        var eventColors = [
            '#FFFFFF',
            '#EEF0FB',
            '#DEE2F7',
            '#CDD3F3',
            '#BDC4EF',
            '#ACB6EC',
            '#9CA7E8',
            '#8B99E4',
            '#7B8AE0',
            '#6A7BDC',
            '#5064D6',
            '#495ED4',
            '#394FD0'
        ];
        tokensData.listOfEvent.forEach(function (tokenEvent) {
            if (!eventNumbers.includes(Number(tokenEvent.eventId))) {
                eventNumbers.push(Number(tokenEvent.eventId));
            }
        });
        eventNumbers.sort(function (a, b) { return a - b; });
        // Create tokens - link source/target compared with token event source/target
        tokenLinks.forEach(function (tokenLink) {
            tokensData.listOfEvent.forEach(function (tokenEvent) {
                var condition = tokenEvent.source === tokenLink.attributes.source.id && tokenEvent.target === tokenLink.attributes.target.id;
                if ((tokenEvent.complete > tokenEvent.start) && condition) {
                    // Get token color
                    var eventColorIndex = eventNumbers.indexOf(Number(tokenEvent.eventId));
                    var tokenColor = (eventColorIndex === -1) ? '#394FD0' : eventColors[eventColorIndex];
                    // Create token graph element
                    var tokenElement = new Token({
                        attrs: { body: { fill: tokenColor } },
                        eventName: tokenEvent.eventName,
                        caseId: tokenEvent.caseId
                    });
                    tokenElement.setText(tokenEvent.eventId);
                    tokenElements.push(tokenElement);
                    // Create token data
                    var token = {
                        data: {
                            id: "follow-".concat(tokenEvent.caseId, "-").concat(tokenEvent.start),
                            currentLutIndex: 0,
                            lut: [],
                            start: tokenEvent.start,
                            end: tokenEvent.complete,
                            element: tokenElement
                        }
                    };
                    // If range time is between token start/end time, update token position, if not, hide token
                    token.data.move = function (value) {
                        if ((value >= this.start) && (value <= this.end)) {
                            var index = this.currentLutIndex;
                            var lut = this.lut[index];
                            if (lut) {
                                this.element.set('hidden', false);
                                var x = lut.x, y = lut.y;
                                // Update position, and centre token on path (Minus half of token width and height)
                                this.element.position(x - 12, y - 8);
                                this.element.rotate(lut.rotation, true);
                            }
                            var position = ((value - this.start) / (this.end - this.start)) * 1000;
                            this.currentLutIndex = Math.round(position);
                        }
                        else {
                            this.element.set('hidden', true);
                        }
                    };
                    var allPoints = [];
                    if (allPoints) {
                        var p = new g.Polyline(tokenLink.get('points'));
                        var l = p.length();
                        // Calculate token x,y positions
                        for (var i = 0; i <= 1; i += 0.001) {
                            var lut = {
                                x: 0,
                                y: 0,
                                rotation: 0
                            };
                            var point = p.pointAtLength(i * l);
                            lut.x = point.x;
                            lut.y = point.y;
                            allPoints.push(lut);
                        }
                        // Calculate token rotation using x,y positions
                        var length_1 = allPoints.length;
                        for (var i = 0; i < length_1; i++) {
                            var lut = allPoints[i];
                            var nextLut = allPoints[i + 1];
                            if (nextLut) {
                                // Calculate angle of line between pair of x,y points
                                var line = new g.Line(lut, nextLut);
                                var angle = Math.round(line.angle());
                                var angleNormalized = g.normalizeAngle(((angle + 90) % 180) - 90);
                                lut.rotation = angleNormalized;
                            }
                            else {
                                // Set rotation for last x,y position
                                lut.rotation = allPoints[i - 1].rotation;
                            }
                        }
                        token.data.lut = allPoints;
                    }
                    tokens.push(token);
                }
            });
        });
        graph.addCells(tokenElements);
    }
    // Register events
    paper.on('blank:pointerdown', function (evt) { return scroller.startPanning(evt); });
    paper.on('blank:mousewheel', function (evt, ox, oy, delta) {
        evt.preventDefault();
        scroller.zoom(delta * 0.2, { min: 0.4, max: 3, grid: 0.2, ox: ox, oy: oy });
    });
    // Create tooltips
    new ui.Tooltip({
        rootTarget: paper.cells,
        target: '.joint-type-app-token',
        content: function (element) {
            var attributes = paper.findView(element).model.attributes;
            return "Event: ".concat(attributes.eventName, "<br>ID: ").concat(attributes.caseId);
        },
        padding: 10
    });
};
